import React, { Fragment } from 'react';
import { shape, oneOfType, func, instanceOf } from 'prop-types';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useQuery, useMutation } from '@apollo/react-hooks';
import styled from 'styled-components/macro';
import MediaQuery from 'react-responsive';
import { withStyles } from '@material-ui/core/styles';
import idx from 'idx';
import { useStoreState } from 'easy-peasy';

import ContentWrapper from '../../components/ContentWrapper';
import Filters, { filterKeys } from '../../components/Filters';
import MobileFilters from '../../components/MobileFilters';
import GET_PRODUCTS, { DELETE_PRODUCT } from './product-query';
import Table from '../../components/Table';
import Loading from '../../components/Loading';
import { useSnackbar } from '../../contexts/SnackbarContext';
import withAuth from '../../containers/withAuth';

const FilterContainer = styled.div`
  background: #f0f0f0;
  padding: 10px 10px 20px 10px;
  display: flex;
  flex-shrink: 0;
  align-self: stretch;
  justify-content: center;
`;

const FilterTitle = styled.h2`
  font-size: 24px;
  font-weight: 300;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const PRODUCT_TYPE_NORMAL = 'normal';
const PRODUCT_TYPE_RS = 'rs';

const getVariables = (where) => {
  const { brand, crusher_type, crusher, group } = where;
  const productType =
    where.productType || where.product_type || PRODUCT_TYPE_NORMAL;

  let whereFilter = buildFilter({ brand, crusher_type, crusher, group });

  // Filter by metso_id: RS products end with -RS, normal do not
  if (productType === PRODUCT_TYPE_RS) {
    const rsCondition = { metso_id: { _ilike: '%-RS' } };
    whereFilter = whereFilter._and
      ? { _and: [...whereFilter._and, rsCondition] }
      : { _and: [rsCondition] };
  } else if (productType === PRODUCT_TYPE_NORMAL) {
    const normalCondition = { _not: { metso_id: { _ilike: '%-RS' } } };
    whereFilter = whereFilter._and
      ? { _and: [...whereFilter._and, normalCondition] }
      : { _and: [normalCondition] };
  }

  const hasProductTypeFilter =
    productType === PRODUCT_TYPE_RS || productType === PRODUCT_TYPE_NORMAL;
  const indexes = Object.keys(where).map((key) => filterKeys.indexOf(key));
  const filterLevel = Math.max(...indexes, -1);
  const effectiveLevel = hasProductTypeFilter
    ? Math.max(filterLevel, 0)
    : filterLevel;
  if (effectiveLevel < 0 && Object.keys(where).length === 0) {
    return {};
  }

  const crusherFilter = buildFilter({ brand, crusher_type });
  const groupFilter = buildFilter({ brand, crusher_type, crusher });
  const crusherTypeFilter = buildFilter({ brand });

  const includes = {};
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i <= effectiveLevel + 1; ++i) {
    if (filterKeys[i]) {
      includes[`includes${filterKeys[i]}`] = true;
    }
  }
  return {
    where: whereFilter,
    crusherFilter,
    groupFilter,
    crusherTypeFilter,
    ...includes,
  };
};

export const columns = [
  {
    width: 120,
    flexShrink: 0,
    label: 'Metso id',
    dataKey: 'metso_id',
    numeric: true,
  },
  {
    width: 120,
    flexShrink: 0,
    label: 'Replace QTY',
    dataKey: 'replace_quantity',
    numeric: true,
  },
  {
    width: 220,
    flexShrink: 0,
    label: 'Description',
    dataKey: 'description',
  },
  {
    width: 120,
    flexShrink: 0,
    label: 'Catalog id',
    dataKey: 'catalog_id',
  },
  {
    width: 120,
    flexShrink: 0,
    label: 'Crusher',
    dataKey: 'crusher',
  },
  {
    width: 180,
    flexShrink: 0,
    label: 'OEM Reference',
    dataKey: 'oem',
  },
  {
    width: 120,
    flexShrink: 0,
    label: 'Group',
    dataKey: 'group',
  },
  {
    width: 120,
    flexShrink: 0,
    label: 'Crusher type',
    dataKey: 'crusher_type',
  },
  {
    width: 120,
    flexShrink: 0,
    label: 'Brand',
    dataKey: 'brand',
  },
];

const TableWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  width: 100%;
`;

const MobileFilterWrapper = styled.div`
  align-items: center;
  flex-direction: column;
  display: flex;
`;

function buildFilter(fields) {
  const conditions = [];

  if (fields.brand) conditions.push({ brand: { _eq: fields.brand } });
  if (fields.crusher_type)
    conditions.push({ crusher_type: { _eq: fields.crusher_type } });
  if (fields.crusher) conditions.push({ crusher: { _eq: fields.crusher } });
  if (fields.group) conditions.push({ group: { _eq: fields.group } });

  return conditions.length ? { _and: conditions } : {};
}

const Products = ({ location, scrollableDiv, history, user }) => {
  const { showSnackbar } = useSnackbar();
  const isEditMode = useStoreState((state) => state.editMode.isEditMode);

  // parse search params
  const where = qs.parse(location.search, { ignoreQueryPrefix: true });

  // Fetch distinct filters by selected value
  const { data, loading, error, refetch } = useQuery(GET_PRODUCTS, {
    variables: getVariables(where),
  });

  // Delete product mutation
  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: getVariables(where) }],
  });

  const handleDeleteProducts = async (selectedIds) => {
    try {
      const productsToDelete = products.filter((product) =>
        selectedIds.includes(product.id),
      );

      if (productsToDelete.length === 0) {
        showSnackbar({
          message: 'No products selected for deletion.',
          variant: 'warning',
        });
        return;
      }

      const promises = productsToDelete.map((product) =>
        deleteProduct({
          variables: { uuid_id: product.uuid_id },
        }),
      );

      await Promise.all(promises);
      // Refetch the products list
      await refetch();

      // Show success message
      showSnackbar({
        message: `${productsToDelete.length} product(s) deleted successfully!`,
        variant: 'success',
      });
    } catch (_error) {
      showSnackbar({
        message: 'Failed to delete products. Please try again.',
        variant: 'error',
      });
    }
  };

  const { products, productsAggregate, ...filtersList } = data || {
    products: [],
  };

  const productsWithOrderQuantity = products.map((p) => ({
    ...p,
    order_quantity: null,
  }));

  if (error) {
    const message = error.message || 'Something went wrong';
    const detail =
      error.graphQLErrors?.[0]?.message || error.networkError?.message;
    return (
      <ContentWrapper>
        <h1>Error occurred</h1>
        <p>{message}</p>
        {detail && <p>{detail}</p>}
      </ContentWrapper>
    );
  }
  if (!data && loading) {
    return <Loading />;
  }

  return (
    <Fragment>
      <FilterContainer>
        <ContentWrapper>
          <MediaQuery minWidth={768}>
            {(matches) => {
              if (matches) {
                return (
                  <Fragment>
                    <FilterTitle>Filter</FilterTitle>
                    <Filters where={where} filters={filtersList} />
                  </Fragment>
                );
              }
              return (
                <MobileFilterWrapper>
                  <FilterTitle>Filter a wear parts from catalogue</FilterTitle>
                  <MobileFilters where={where} filters={filtersList} />
                </MobileFilterWrapper>
              );
            }}
          </MediaQuery>
        </ContentWrapper>
      </FilterContainer>
      {scrollableDiv && scrollableDiv.current && (
        <TableWrapper>
          <ContentWrapper>
            {loading ? (
              <Loading />
            ) : (
              <Table
                showCollect
                productsCount={
                  idx(productsAggregate, (_) => _.aggregate.count) || 0
                }
                data={productsWithOrderQuantity}
                columns={columns}
                scrollableDiv={scrollableDiv}
                onRowClick={(p) => history.push(`product/${p.rowData.uuid_id}`)}
                location={location}
                deleteFromCollection={
                  user && user.userRole === 'manager' && isEditMode
                    ? handleDeleteProducts
                    : undefined
                }
              />
            )}
          </ContentWrapper>
        </TableWrapper>
      )}
    </Fragment>
  );
};

Products.propTypes = {
  scrollableDiv: oneOfType([func, shape({ current: instanceOf(Element) })])
    .isRequired,
  history: shape({}).isRequired,
  location: shape({}).isRequired,
  user: shape({
    userRole: PropTypes.string,
  }),
};

const styles = (theme) => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  },
});

export default withAuth(withStyles(styles)(Products));
